import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ChevronsLeft, ChevronsRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
	Dialog,
	DialogContent,
} from '@/components/ui/dialog'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useLayoutStore } from '@/stores/layout'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { menuItems } from '@/types/menu'
import { useFilteredMenu } from '@/hooks/useFilteredMenu'
import { SidebarMenuItem } from './SidebarMenuItem'

// Logo 组件
function Logo({ collapsed }: { collapsed: boolean }) {
	return (
		<Link to="/" className="flex items-center gap-2 px-3 h-14">
			<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent text-accent-foreground">
				<svg
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					className="h-5 w-5"
				>
					<path d="M12 2L2 7l10 5 10-5-10-5z" />
					<path d="M2 17l10 5 10-5" />
					<path d="M2 12l10 5 10-5" />
				</svg>
			</div>
			{!collapsed && (
				<span className="text-lg font-semibold tracking-tight">Carrot</span>
			)}
		</Link>
	)
}

// 折叠按钮
function CollapseButton({ collapsed }: { collapsed: boolean }) {
	const { toggleSidebar } = useLayoutStore()

	return (
		<Button
			variant="ghost"
			size="icon"
			onClick={toggleSidebar}
			className="h-8 w-8 rounded-lg"
			aria-label={collapsed ? '展开侧边栏' : '折叠侧边栏'}
		>
			{collapsed ? (
				<ChevronsRight className="h-4 w-4" />
			) : (
				<ChevronsLeft className="h-4 w-4" />
			)}
		</Button>
	)
}

// 侧边栏内容
interface SidebarContentProps {
	collapsed?: boolean
}

function SidebarContent({ collapsed = false }: SidebarContentProps) {
	const filteredMenuItems = useFilteredMenu(menuItems)

	return (
		<TooltipProvider delayDuration={0}>
			<div className="flex h-full flex-col">
				{/* Logo 区域 */}
				<Logo collapsed={collapsed} />

				<Separator />

				{/* 菜单区域 */}
				<nav className="flex-1 space-y-1 overflow-y-auto p-3 scrollbar-thin">
					{filteredMenuItems.map((item) => (
						<SidebarMenuItem key={item.id} item={item} collapsed={collapsed} />
					))}
				</nav>

				<Separator />

				{/* 底部折叠按钮 */}
				<div className={cn(
					"flex items-center p-3",
					collapsed ? "justify-center" : "justify-end"
				)}>
					<CollapseButton collapsed={collapsed} />
				</div>
			</div>
		</TooltipProvider>
	)
}

// 桌面端侧边栏
function DesktopSidebar() {
	const { sidebarCollapsed } = useLayoutStore()

	return (
		<aside
			className={cn(
				"hidden md:flex flex-col h-screen border-r bg-card transition-all duration-300 ease-in-out",
				sidebarCollapsed ? "w-[68px]" : "w-[240px]"
			)}
		>
			<SidebarContent collapsed={sidebarCollapsed} />
		</aside>
	)
}

// 移动端抽屉侧边栏
function MobileSidebar() {
	const { sidebarOpen, setSidebarOpen } = useLayoutStore()
	const isMobile = useIsMobile()

	// 路由变化时关闭抽屉
	useEffect(() => {
		if (!isMobile) {
			setSidebarOpen(false)
		}
	}, [isMobile, setSidebarOpen])

	return (
		<Dialog open={sidebarOpen} onOpenChange={setSidebarOpen}>
			<DialogContent
				className="p-0 h-screen w-[280px] max-w-[280px] !translate-x-0 !translate-y-0 !left-0 !top-0 rounded-none"
			>
				<SidebarContent collapsed={false} />
			</DialogContent>
		</Dialog>
	)
}

export function Sidebar() {
	const isMobile = useIsMobile()

	return (
		<>
			{!isMobile && <DesktopSidebar />}
			{isMobile && <MobileSidebar />}
		</>
	)
}

export { SidebarContent }
